import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  readResultPendingProjects, 
  cacheResultPendingProjects,
  warmResultPendingProjects
} from '../../utils/influencerResultCache';
import ProjectDetailModal from '../marketer/project/modals/ProjectDetailModal';
import ResultSubmitModal from './project/modals/ResultSubmitModal';
import ResultSubmitConfirmModal from './project/modals/ResultSubmitConfirmModal';
import { usePoints } from '../../contexts/PointContext';
import '../../styles/common/ib-layout.css';
import '../../styles/common/ib-forms.css';

const preloadImage = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

function InfluencerResultSend() {
  const { refreshPoints } = usePoints();
  const [uid, setUid] = useState(window.__ME__?.id);
  
  useEffect(() => {
    const currentUid = window.__ME__?.id;
    if (currentUid !== uid) {
      setUid(currentUid);
    }
    
    const handleUserReady = (event) => {
      const userId = event.detail?.id;
      if (userId) {
        setUid(userId);
      }
    };
    
    if (!window.__USER_READY_LISTENERS__) {
      window.__USER_READY_LISTENERS__ = [];
    }
    window.__USER_READY_LISTENERS__.push(handleUserReady);
    
    return () => {
      if (window.__USER_READY_LISTENERS__) {
        const idx = window.__USER_READY_LISTENERS__.indexOf(handleUserReady);
        if (idx > -1) {
          window.__USER_READY_LISTENERS__.splice(idx, 1);
        }
      }
    };
  }, [uid]);
  
  const initialProjects = useMemo(() => {
    return uid ? (readResultPendingProjects(uid) || []) : [];
  }, [uid]);
  
  const hasCache = uid && initialProjects.length > 0;
  
  const [projects, setProjects] = useState(initialProjects);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(!hasCache);
  const [error, setError] = useState(null);
  
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitData, setSubmitData] = useState(null);
  
  const currentProject = projects[currentIndex] || null;
  
  const fetchProjects = useCallback(async () => {
    if (!uid) return;
    
    const cached = readResultPendingProjects(uid);
    if (cached) {
      setProjects(cached);
      setLoading(false);
      refreshPoints();
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch('/api/influencer/mybiz/projects/result-pending', {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '프로젝트 목록 조회 실패');
      }
      
      setProjects(data.projects || []);
      cacheResultPendingProjects(uid, data.projects || []);
      
      refreshPoints();
      
      if (data.projects && data.projects.length > 0 && data.projects[0].coverImage) {
        preloadImage(data.projects[0].coverImage);
      }
      
      if (data.projects && data.projects.length > 1 && data.projects[1].coverImage) {
        setTimeout(() => preloadImage(data.projects[1].coverImage), 100);
      }
      
    } catch (e) {
      console.error('프로젝트 목록 조회 오류:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid, refreshPoints]);
  
  useEffect(() => {
    if (uid && !hasCache) {
      fetchProjects();
    }
  }, [uid, hasCache, fetchProjects]);
  
  useEffect(() => {
    const handleUpdate = () => {
      if (uid) {
        warmResultPendingProjects(uid)
          .then(freshData => {
            if (freshData) {
              setProjects(freshData);
            }
          })
          .catch(() => {
            fetchProjects();
          });
      }
    };
    
    window.addEventListener('result-list-update', handleUpdate);
    
    return () => {
      window.removeEventListener('result-list-update', handleUpdate);
    };
  }, [uid, fetchProjects]);
  
  const getServiceTypeText = (type) => {
    const map = {
      delivery: '제품 제공',
      purchase: '제품 미제공',
      visit: '장소 광고',
      free: '광고주 자율방식',
      custom: '광고주 자율방식'
    };
    return map[type] || type;
  };
  
  const getContentTypeText = (type) => {
    const map = {
      post: '포스트(피드)',
      video: '영상(릴스/쇼츠)',
      free: '자유'
    };
    return map[type] || type;
  };
  
  const getProductName = (project) => {
    if (!project) return '광고 대상';
    return project.productName || project.product?.name || project.product?.placeName || project.product?.customTitle || '광고 대상';
  };
  
  const [showRequestModal, setShowRequestModal] = useState(false);

  // 상태별 검수 배너 (기존 8분기 문구 유지 — v4 sbanner 문법으로 표시)
  const getStatusBanner = useCallback((project) => {
    const now = new Date();
    const submitEnd = project.submitEnd ? new Date(project.submitEnd) : null;
    const isDeadlinePassed = submitEnd && submitEnd < now;
    const status = project.resultStatus;
    const hasCPS = project.perPurchaseReward?.use;

    // 1. 마감일 지났는데 미제출 (가장 긴급)
    if (isDeadlinePassed && status === 'none') {
      return { show: true, emoji: '🚨', tone: 'red', text: '결과물 제출 마감일이 지났습니다! 셀프애드 팀에 문의해주세요.' };
    }
    // 2. 만료 상태 (expired)
    if (status === 'expired') {
      return { show: true, emoji: '⚠️', tone: 'amber', text: '제출 기한이 만료되었습니다. 셀프애드 팀이 결과물을 확인 중입니다.' };
    }
    // 3. 광고주가 거절한 경우
    if (status === 'rejected') {
      return { show: true, emoji: '🔍', tone: 'amber', text: '광고주가 결과물을 거절했습니다. 셀프애드 팀이 재검토 중입니다.' };
    }
    // 4. 셀프애드 팀 검토 중
    if (status === 'admin_reviewing') {
      return { show: true, emoji: '👀', tone: 'blue', text: '셀프애드 팀이 결과물을 검토하고 있습니다.' };
    }
    // 5. 승인 완료 (리워드 지급됨)
    if (status === 'approved' || status === 'admin_approved') {
      return hasCPS
        ? { show: true, emoji: '✅', tone: 'mint', text: '기본 리워드가 지급되었습니다. CPS 리워드는 결산일에 정산됩니다.' }
        : { show: true, emoji: '🎉', tone: 'mint', text: '결과물이 승인되어 리워드가 지급되었습니다!' };
    }
    // 6. 셀프애드 팀이 거절 (계약 위반)
    if (status === 'admin_rejected') {
      return { show: true, emoji: '❌', tone: 'red', text: '결과물 미제출로 계약이 위반되었습니다. 문의사항은 셀프애드 팀에 연락해주세요.' };
    }
    // 7. 제출 완료 - 광고주 검토 중
    if (status === 'submitted') {
      return { show: true, emoji: '⏳', tone: 'blue', text: '결과물 제출 완료! 광고주가 검토 중입니다.' };
    }
    // 8. 배너 표시 안함 (none이지만 마감일 안 지남)
    return { show: false };
  }, []);
  
  const handleOpenDetailModal = () => {
    setShowSubmitModal(false);
    setShowConfirmModal(false);
    setTimeout(() => setShowDetailModal(true), 100);
  };
  
  const handleOpenSubmitModal = () => {
    setShowDetailModal(false);
    setShowConfirmModal(false);
    setTimeout(() => setShowSubmitModal(true), 100);
  };
  
  const handleOpenConfirmModal = (data) => {
    setSubmitData(data);
    setShowConfirmModal(true);
  };
  
  const handleSubmitSuccess = () => {
    const updatedProjects = projects.map((proj, idx) => {
      if (idx === currentIndex) {
        return {
          ...proj,
          resultStatus: 'submitted',
          resultSubmittedAt: new Date().toISOString()
        };
      }
      return proj;
    });
    
    setProjects(updatedProjects);
    cacheResultPendingProjects(uid, updatedProjects);
    
    if (window.warmupInfluencer) {
      window.warmupInfluencer()
        .then(() => {
          window.dispatchEvent(new CustomEvent('project-list-update'));
        })
        .catch(() => {
          window.dispatchEvent(new CustomEvent('project-list-update'));
        });
    }
    
    alert('결과물이 제출되었습니다.');
  };
  
  const shortDate = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return `${x.getMonth() + 1}/${x.getDate()}`;
  };

  // 상태 배지 (카드 우측)
  const statusBadge = (project) => {
    const now = new Date();
    const submitEnd = project.submitEnd ? new Date(project.submitEnd) : null;
    const isDeadlinePassed = submitEnd && submitEnd < now;
    const status = project.resultStatus;
    if (status === 'none' && !isDeadlinePassed) {
      const d = project.daysUntilDeadline;
      return { pill: true, red: d != null && d <= 1, label: d != null ? `제출 마감 D-${Math.max(0, d)}` : '제출 대기' };
    }
    if (status === 'none' && isDeadlinePassed) return { cls: 'badge--red', label: '마감 지남' };
    if (status === 'submitted') return { cls: 'badge--blue', label: '검수 중' };
    if (status === 'rejected') return { cls: 'badge--amber', label: '재검토 중' };
    if (status === 'admin_reviewing') return { cls: 'badge--blue', label: '팀 검토 중' };
    if (status === 'approved' || status === 'admin_approved') return { cls: 'badge--mint', label: '승인 완료' };
    if (status === 'admin_rejected') return { cls: 'badge--red', label: '계약 위반' };
    if (status === 'expired') return { cls: 'badge--gray', label: '기한 만료' };
    return { cls: 'badge--gray', label: '진행 중' };
  };

  if (loading) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">결과물 제출하기</h2>
          <p className="mb-page-description">게시물 링크를 제출하면 광고주 검수 후 보상이 지급돼요.</p>
        </div>
        <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
          결과물 제출 대상을 불러오는 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">결과물 제출하기</h2>
        </div>
        <div className="mb-card" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#d13b3b', marginBottom: 6 }}>오류가 발생했어요</div>
          <div style={{ fontSize: 14, color: '#6b7686', marginBottom: 18 }}>{error}</div>
          <button type="button" onClick={fetchProjects} className="mb-btn mb-btn-secondary">다시 시도</button>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div>
        <div className="mb-page-header">
          <h2 className="mb-page-title">결과물 제출하기</h2>
          <p className="mb-page-description">게시물 링크를 제출하면 광고주 검수 후 보상이 지급돼요.</p>
        </div>
        <div className="mb-card" style={{ padding: '72px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>
            제출할 결과물이 없어요
          </div>
          <div style={{ fontSize: 14, color: '#6b7686' }}>
            계약한 프로젝트의 결과물 제출 건이 여기에 표시돼요
          </div>
        </div>
      </div>
    );
  }

  const now = new Date();
  const toSubmitCount = projects.filter((p) => {
    const submitEnd = p.submitEnd ? new Date(p.submitEnd) : null;
    return p.resultStatus === 'none' && !(submitEnd && submitEnd < now);
  }).length;

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">결과물 제출하기</h2>
        <p className="mb-page-description">게시물 링크를 제출하면 광고주 검수 후 보상이 지급돼요.</p>
      </div>

      {/* 카운트 히어로 */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
        <b style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.04em', color: '#0a8f62', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {toSubmitCount}
        </b>
        <span style={{ fontSize: 14, color: '#6b7686', fontWeight: 600 }}>
          건의 결과물이 제출을 기다리고 있어요
        </span>
      </div>

      {projects.map((project, idx) => {
        const badge = statusBadge(project);
        const banner = getStatusBanner(project);
        const submitEnd = project.submitEnd ? new Date(project.submitEnd) : null;
        const canSubmit = project.resultStatus === 'none' && !(submitEnd && submitEnd < now);
        const isDone = project.resultStatus === 'approved' || project.resultStatus === 'admin_approved';
        return (
          <div
            className={`subcard${canSubmit ? ' subcard--warn' : ''}`}
            style={isDone ? { opacity: 0.85 } : undefined}
            key={project.applicationId || project._id}
          >
            <div className="subcard__head" style={{ marginBottom: banner.show || canSubmit ? 12 : 0 }}>
              <span className="mbh-pcard__logo" style={{ width: 40, height: 40, fontSize: 13 }} aria-hidden="true">
                {project.coverImage
                  ? <img src={project.coverImage} alt="" decoding="async" />
                  : getProductName(project).charAt(0)}
              </span>
              <div className="subcard__who">
                <div className="subcard__name">{getProductName(project)}</div>
                <div className="subcard__sns">
                  {[
                    `NO.${project.projectNumber}`,
                    getServiceTypeText(project.serviceType),
                    getContentTypeText(project.contentType),
                    project.resultSubmittedAt
                      ? `${shortDate(project.resultSubmittedAt)} 제출`
                      : `승인 시 ${Number(project.rewardPointPerCreator || 0).toLocaleString()}P`,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              {badge.pill
                ? <span className={`deadline${badge.red ? ' deadline--red' : ''}`}>{badge.label}</span>
                : <span className={`badge ${badge.cls}`}>{badge.label}</span>}
            </div>

            {/* 검수 상태 배너 */}
            {banner.show && (
              <div className={`sbanner sbanner--${banner.tone}`} style={{ marginBottom: canSubmit ? 12 : 0 }}>
                <i>{banner.emoji}</i>
                <span>{banner.text}</span>
              </div>
            )}

            {/* 제출 가능: 요청사항 요약 + 제출 버튼 (입력은 기존 제출 모달 재사용) */}
            {canSubmit && (
              <>
                {project.marketerRequest && (
                  <div className="reqbox" style={{ marginBottom: 12 }}>
                    <b>광고주 요청사항</b>
                    <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'pre-wrap' }}>
                      {project.marketerRequest}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCurrentIndex(idx); setShowRequestModal(true); }}
                      style={{ border: 0, background: 'none', color: '#0a8f62', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, padding: 0, marginTop: 4 }}
                    >
                      전체 내용 보기 →
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="mb-btn mb-btn-secondary"
                    onClick={() => { setCurrentIndex(idx); handleOpenDetailModal(); }}
                  >
                    상세보기
                  </button>
                  <button
                    type="button"
                    className="mb-btn mb-btn-primary"
                    style={{ flex: 1 }}
                    onClick={() => { setCurrentIndex(idx); handleOpenSubmitModal(); }}
                  >
                    링크 제출하기
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* 제출 가이드 */}
      <div className="mb-card">
        <h3 className="mb-section-title">제출 전 확인해주세요</h3>
        <div className="checkline">
          <i>✓</i>
          <div>
            <b>게시물 URL 필수</b>
            <span>업로드한 콘텐츠의 링크를 제출해주세요 · 여러 계정으로 진행했다면 계정별로 모두 첨부</span>
          </div>
        </div>
        <div className="checkline">
          <i>✓</i>
          <div>
            <b>스크린샷 (선택)</b>
            <span>업로드한 콘텐츠의 화면 캡처를 첨부하면 확인이 빨라져요</span>
          </div>
        </div>
        <div className="checkline">
          <i>✓</i>
          <div>
            <b>통계 자료 (선택)</b>
            <span>조회수·좋아요 등의 통계 화면을 첨부하면 승인에 도움이 돼요</span>
          </div>
        </div>
        <div className="note note--blue">제출 후 광고주가 7일 안에 응답하지 않으면 자동 승인되고 보상이 지급돼요.</div>
      </div>

      {/* 추가 요청사항 전체 보기 모달 */}
      {showRequestModal && currentProject?.marketerRequest && (
        <div className="mbm-overlay" onClick={() => setShowRequestModal(false)}>
          <div
            className="mb-card mbm"
            style={{ width: 640, maxWidth: '92vw', margin: 0, padding: 28, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-section-title" style={{ marginBottom: 14 }}>광고주 요청사항</h3>
            <div className="reqbox" style={{ marginBottom: 0, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
              {currentProject.marketerRequest}
            </div>
            <div className="mbm-foot" style={{ marginTop: 16 }}>
              <button type="button" className="mb-btn mb-btn-primary" onClick={() => setShowRequestModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && currentProject && (
        <ProjectDetailModal
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          project={{
            ...currentProject,
            product: currentProject.product,
            description: currentProject.description,
            recruit: currentProject.recruit
          }}
        />
      )}

      {showSubmitModal && currentProject && (
        <ResultSubmitModal
          open={showSubmitModal}
          onClose={() => setShowSubmitModal(false)}
          onOpenConfirmModal={handleOpenConfirmModal}
          project={currentProject}
        />
      )}

      {showConfirmModal && currentProject && submitData && (
        <ResultSubmitConfirmModal
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onBack={() => {
            setShowConfirmModal(false);
            setTimeout(() => setShowSubmitModal(true), 100);
          }}
          onSubmitSuccess={handleSubmitSuccess}
          projectId={currentProject._id}
          urls={submitData.urls}
          files={submitData.files}
          note={submitData.note}
          submitEnd={currentProject.submitEnd}
        />
      )}
    </div>
  );
}

export default InfluencerResultSend;
